import { useEffect, useMemo, useState } from "react";
import { FiDownload, FiEye, FiSearch } from "react-icons/fi";
import api from "../services/api";
import IncentiveSummary from "../components/dashboard/IncentiveSummary";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const Incentive = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [serviceFee, setServiceFee] = useState(0);
  const [minWithdrawal, setMinWithdrawal] = useState(0);
  const [maxWithdrawal, setMaxWithdrawal] = useState(0);
  const [statusOptions, setStatusOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("requested_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [requestAmount, setRequestAmount] = useState("");
  const [requestError, setRequestError] = useState("");
  const [requestSuccess, setRequestSuccess] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchIncentives = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/incentives", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            status_id: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load incentive data.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
        setSummary(data.summary || null);
        setServiceFee(data.service_fee || 0);
        setMinWithdrawal(data.min_withdrawal || 0);
        setMaxWithdrawal(data.max_withdrawal || 0);
        setStatusOptions(data.status_options || []);
      } catch (error) {
        setLoadError("Failed to load incentive data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchIncentives();
  }, [query, dateFrom, dateTo, statusFilter, sortBy, sortDir, page, perPage, refreshKey]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleRequest = async () => {
    setRequestError("");
    setRequestSuccess("");
    setIsSubmitting(true);
    try {
      const amount = Number(requestAmount);
      if (!amount) {
        setRequestError("Please enter an amount.");
        return;
      }

      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setRequestError("Missing access token.");
        return;
      }

      const response = await api.post(
        "/incentives/request",
        { amount },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status !== "success") {
        setRequestError(response.data?.message || "Failed to submit payout request.");
        return;
      }

      setRequestSuccess(response.data?.message || "Payout request submitted.");
      setRequestAmount("");
      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message && typeof message === "object") {
        setRequestError(Object.values(message).flat().join(" "));
      } else {
        setRequestError(message || "Failed to submit payout request.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = async (id) => {
    setIsViewOpen(true);
    setViewError("");
    setViewData(null);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setViewError("Missing access token.");
        return;
      }

      const response = await api.get(`/incentives/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setViewError("Failed to load payout details.");
        return;
      }

      setViewData(response.data?.data || null);
    } catch (error) {
      setViewError("Failed to load payout details.");
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      // hour: "2-digit",
      // minute: "2-digit",
      // hour12: true,
    });
  };

  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return "₱ 0.00";
    }
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("₱", "₱ ");
  };

  const getStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "paid") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-red-100 text-red-700";
    }
    if (normalized === "rejected") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "for approval") {
      return "bg-amber-100 text-amber-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const renderLevelList = (levels) => {
    if (!levels) {
      return null;
    }
    return (
      <div className="text-xs text-slate-500">
        <span className="font-semibold text-slate-600">Levels</span>
        <div className="mt-1 space-y-0.5">
          {[1, 2, 3, 4, 5].map((level) => (
            <div key={level}>
              {level} - {formatAmount(levels[level] || 0)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const totalSummary = summary?.total_amount || 0;
  const releasedSummary = summary?.released_amount || 0;
  const requestedSummary = summary?.requested_amount || 0;
  const availableSummary = summary?.available_amount || 0;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Incentive
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Incentive & Payout
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Track your incentives, request payouts, and monitor approval status.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
        <IncentiveSummary refreshKey={refreshKey} />
        {/*
        <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
          <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
            <span className="text-xl">💰</span>
            Incentive Summary
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Total
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatAmount(totalSummary)}
              </p>
              {renderLevelList(summary?.levels?.total)}
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Released
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatAmount(releasedSummary)}
              </p>
              {renderLevelList(summary?.levels?.released)}
            </div>
            <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">
                Requested
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatAmount(requestedSummary)}
              </p>
              {renderLevelList(summary?.levels?.requested)}
            </div>
            <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">
                For Request
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatAmount(availableSummary)}
              </p>
              {renderLevelList(summary?.levels?.available)}
            </div>
          </div>
        </div>
        */}

        <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
          <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
            <span className="text-xl">💳</span>
            Request for Incentive
          </div>
          <div className="space-y-4">
            <input
              type="number"
              min="0"
              value={requestAmount}
              onChange={(event) => setRequestAmount(event.target.value)}
              placeholder="Enter amount"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <div className="text-xs text-slate-500">
              Minimum withdrawal: {formatAmount(minWithdrawal)} • Maximum withdrawal:{" "}
              {formatAmount(maxWithdrawal)} • Service fee: {formatAmount(serviceFee)}
            </div>
            <button
              type="button"
              onClick={() => setIsPolicyOpen(true)}
              className="text-sm font-semibold text-[var(--brand-blue)] underline decoration-dotted underline-offset-4"
            >
              View Policy
            </button>

            {requestError && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {requestError}
              </div>
            )}
            {requestSuccess && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {requestSuccess}
              </div>
            )}

            <button
              type="button"
              onClick={handleRequest}
              disabled={isSubmitting}
              className="w-full rounded-2xl bg-[var(--brand-orange)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Submitting..." : "Request"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <span className="text-xl">🕒</span>
          Payout History
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </form>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>Display</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            className="min-w-[180px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">All Status</option>
            {statusOptions.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
              <tr>
                {[
                  { key: "requested_at", label: "Date" },
                  { key: "request_id", label: "Request ID" },
                  { key: "amount", label: "Amount" },
                  { key: "status", label: "Status" },
                  { key: "paid_at", label: "Payment Date" },
                  { key: "bank_reference_no", label: "Bank Reference No." },
                ].map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="cursor-pointer px-4 py-3 text-xs font-semibold"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortBy === column.key && (
                        <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={7}>
                    Loading payout history...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={7}>
                    No payout history available.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{formatDateTime(row.requested_at)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {row.request_id || "-"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {formatAmount(row.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(
                          row.status_name
                        )}`}
                      >
                        {row.status_name || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatDateTime(row.paid_at)}</td>
                    <td className="px-4 py-3">{row.bank_reference_no || "-"}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleView(row.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FiEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isPolicyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Policy
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Incentive Payout Policy
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPolicyOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 max-h-[360px] space-y-4 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Incentive Release Policy
              </p>
              <p>
                Incentive requests may be filed anytime through the RMC2 KAAGAPAY
                Emergency Card Portal. Requests filed from the 1st to the 15th
                of the month will be credited on the 20th of the same month.
              </p>
              <p>
                Requests filed from the 16th to the end of the month will be
                credited on the 5th banking day of the following month. If the
                release date falls on a weekend or holiday, it will be moved to
                the next banking day.
              </p>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Minimum Withdrawal & Fees
              </p>
              <p>
                Minimum withdrawal: {formatAmount(minWithdrawal)} per request
              </p>
              <p>
                Maximum withdrawal: {formatAmount(maxWithdrawal)} per request
              </p>
              <p>
                Service fee: {formatAmount(serviceFee)} per request (processing
                and transfer fee)
              </p>
              <p>
                Multiple requests are allowed, subject to the cutoff schedule.
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsPolicyOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isViewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Incentive
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Payout Request Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review the full details for this payout request.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {viewError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {viewError}
              </div>
            )}

            {!viewError && !viewData && (
              <p className="mt-6 text-sm text-slate-400">Loading details...</p>
            )}

            {viewData && (
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {[
                  { label: "Request ID", value: viewData.request_id },
                  { label: "Status", value: viewData.status_name },
                  { label: "Requested Date", value: formatDateTime(viewData.requested_at) },
                  { label: "Amount", value: formatAmount(viewData.amount) },
                  { label: "Service Fee", value: formatAmount(viewData.service_fee) },
                  { label: "Net Amount", value: formatAmount(viewData.net_amount) },
                  { label: "Payment Date", value: formatDateTime(viewData.paid_at) },
                  { label: "Bank Reference No.", value: viewData.bank_reference_no },
                  { label: "Remarks", value: viewData.remarks },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                  >
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      {item.value || "-"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Incentive;
